import { ReactNode, Component } from 'react';
import { Navigation } from './Navigation';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { IntelligentUpgradeSystem } from './IntelligentUpgradeSystem';
import { useAuth } from '../lib/auth-context';
import { LogOut, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { isMarketingPublicPath } from './marketing/marketingStandaloneNav';
import { useEnterpriseOrg } from '../context/EnterpriseOrgContext';
import { EnterpriseTopbarBadge } from './enterprise/EnterpriseTopbarBadge';
import { ConsumerAICopilotGlobal } from './ConsumerAICopilotGlobal';
import { CLOUD_BOOTSTRAP_DONE_KEY } from '../lib/cloud-sync';
import { useEffect, useState } from 'react';
import { useAccountBootstrapReady } from '../lib/use-account-bootstrap-ready';
import {
  generateConsumerLoginSnapshot,
  shouldGenerateLoginSnapshotForSession,
} from '../lib/consumer-login-snapshot';
import { useAppSettings } from '../lib/app-settings-context';
import { DSPage, DSPageSection, DSState, DSStack } from '@/design-system';

interface AppLayoutProps {
  children: ReactNode;
}

// Pages that should not show navigation (onboarding flow, auth flow)
const NO_NAV_PAGES = [
  '/',
  '/auth',
  '/auth/callback',
  '/password-reset',
  '/onboarding',
  '/singles-onboarding',
  '/mode-selection',
  '/email-verified',
  '/launch',
  '/early-access',
  '/investors',
  '/waitlist-blitz',
];

// Public pages that don't require authentication
const PUBLIC_PAGES = [
  '/',
  '/auth',
  '/auth/callback',
  '/email-verified',
  '/launch',
  '/early-access',
  '/investors',
  '/waitlist-blitz',
  '/terms',
  '/privacy',
  '/photo-credits',
  '/about',
  '/faq',
  '/support',
  '/feedback',
  '/password-reset',
];

// Stable error boundary type (must not be declared inside AppLayout render)
class NamedAppLayoutErrorBoundary extends Component<
  { name: string; children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { name: string; children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error(`🚨 AppLayout render error in "${this.props.name}"`, error);
    if (errorInfo?.componentStack) {
      console.error(`AppLayout "${this.props.name}" component stack:`, errorInfo.componentStack);
    }
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

function RouteContentErrorFallback({ onRetry }: { onRetry: () => void }) {
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/auth', { replace: true });
    } catch (e) {
      console.error(e);
      navigate('/auth', { replace: true });
    }
  };

  return (
    <DSPage className="min-h-[50vh] flex items-center justify-center">
      <DSState className="max-w-[520px] w-full text-center" state="error">
        <DSStack size={16}>
          <p className="text-[22px] font-medium">
            This page couldn&apos;t load
          </p>
          <p data-caption="true">
            Something broke while rendering this screen — often fixed by refreshing or signing out and back in.
          </p>
        </DSStack>
        <div className="flex flex-col sm:flex-row gap-2 justify-center pt-4">
          <Button type="button" variant="outline" onClick={onRetry} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Try again
          </Button>
          <Button type="button" variant="default" onClick={() => void handleSignOut()} className="gap-2">
            <LogOut className="h-4 w-4" />
            Sign out
          </Button>
        </div>
      </DSState>
    </DSPage>
  );
}

class RouteContentErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: { componentStack?: string }) {
    console.error('🚨 Route content render error', error);
    if (errorInfo?.componentStack) {
      console.error('Route content component stack:', errorInfo.componentStack);
    }
  }

  render() {
    if (this.state.hasError) {
      return <RouteContentErrorFallback onRetry={() => this.setState({ hasError: false })} />;
    }
    return this.props.children;
  }
}

function safeLocalStorageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function CloudBootstrapBanner() {
  const { user } = useAuth();
  const [phase, setPhase] = useState<'hidden' | 'restoring' | 'done'>('hidden');

  useEffect(() => {
    if (!user) {
      setPhase('hidden');
      return;
    }
    if (typeof window === 'undefined') return;

    let cancelled = false;
    const startedAt = Date.now();
    const initialDone = safeLocalStorageGet(CLOUD_BOOTSTRAP_DONE_KEY) === 'true';
    if (initialDone) {
      setPhase('hidden');
      return;
    }

    setPhase('restoring');

    const poll = () => {
      if (cancelled) return;
      const done = safeLocalStorageGet(CLOUD_BOOTSTRAP_DONE_KEY) === 'true';
      const timedOut = Date.now() - startedAt > 10000;

      if (done || timedOut) {
        setPhase(done ? 'done' : 'hidden');
        if (done) {
          window.setTimeout(() => {
            if (!cancelled) setPhase('hidden');
          }, 1400);
        }
        return;
      }

      window.setTimeout(poll, 150);
    };

    poll();

    return () => {
      cancelled = true;
    };
  }, [user]);

  if (phase === 'hidden') return null;

  return (
    <div className="sticky top-0 z-40 border-b border-slate-200 bg-white/98 px-4 py-2 text-center text-sm font-medium text-neutral-900 shadow-[0_10px_24px_rgba(15,23,42,0.08)] backdrop-blur-[6px]">
      {phase === 'restoring' ? 'Restoring your account data...' : 'Account data restored'}
    </div>
  );
}

export function AppLayout({ children }: AppLayoutProps) {
  const { user, loading, accessToken } = useAuth();
  const { consumerLoginSnapshotEnabled, consumerLoginSnapshotKillSwitch } = useAppSettings();
  useEnterpriseOrg();
  const accountBootstrapReady = useAccountBootstrapReady();
  
  // Safe hook usage - handle case where component is rendered outside Router context
  let location;
  try {
    location = useLocation();
  } catch (error) {
    // If useLocation fails (e.g., in Figma Make preview), just render children
    console.debug('AppLayout: useLocation failed, rendering without navigation', error);
    return <>{children}</>;
  }
  
  // Check if current page is public
  const isPublicPage =
    PUBLIC_PAGES.includes(location.pathname) ||
    isMarketingPublicPath(location.pathname) ||
    location.pathname.startsWith('/community/posts/');
  
  // Check if current page should show navigation
  // Hide navigation for admin pages (any route starting with /admin, /marketing, /debug, or /error-logs)
  const isAdminPage =
    location.pathname === '/admin-demo' ||
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/enterprise/admin') ||
    location.pathname.startsWith('/marketing') ||
    location.pathname.startsWith('/debug') ||
    location.pathname.startsWith('/error-logs');
  
  const shouldShowNav =
    !NO_NAV_PAGES.includes(location.pathname) &&
    !isAdminPage &&
    !isMarketingPublicPath(location.pathname);
  const isEnterpriseShell = location.pathname.startsWith('/enterprise');
  const hasSessionHint = (() => {
    if (typeof window === 'undefined') return false;
    try {
      return Object.keys(window.localStorage).some(
        (k) => k.includes('auth-token') || k.includes('supabase.auth.token'),
      );
    } catch {
      return false;
    }
  })();
  const shouldMountConsumerCopilot =
    (user || hasSessionHint) &&
    !isEnterpriseShell &&
    !isAdminPage &&
    !isMarketingPublicPath(location.pathname) &&
    !NO_NAV_PAGES.includes(location.pathname) &&
    !location.pathname.startsWith('/support');

  const routeBody = <RouteContentErrorBoundary>{children}</RouteContentErrorBoundary>;

  useEffect(() => {
    if (!user || !accessToken || !accountBootstrapReady) return;
    if (!consumerLoginSnapshotEnabled || consumerLoginSnapshotKillSwitch) return;
    if (!shouldGenerateLoginSnapshotForSession(user.id)) return;
    void generateConsumerLoginSnapshot(user.id);
  }, [
    user,
    accessToken,
    accountBootstrapReady,
    consumerLoginSnapshotEnabled,
    consumerLoginSnapshotKillSwitch,
  ]);

  // If user is not authenticated and trying to access protected page, don't show navigation
  // Redirect to auth instead of rendering route body (prevents protected page crashes when stale session hints exist).
  if (!loading && !user && !isPublicPage) {
    return <Navigate to="/auth" replace />;
  }

  if (!shouldShowNav) {
    return (
      <DSPage className="tension-page-bg !p-0" data-enterprise-shell={isEnterpriseShell ? 'true' : 'false'}>
        {shouldMountConsumerCopilot && (
          <NamedAppLayoutErrorBoundary name="ConsumerAICopilotGlobalNoNav">
            <ConsumerAICopilotGlobal />
          </NamedAppLayoutErrorBoundary>
        )}
        {routeBody}
      </DSPage>
    );
  }

  return (
    <DSPage className="tension-page-bg !p-0" data-enterprise-shell={isEnterpriseShell ? 'true' : 'false'}>
      {/* Top Navigation Bar - only show if user is authenticated */}
      {user && !isEnterpriseShell && (
        <NamedAppLayoutErrorBoundary name="Navigation">
          <Navigation variant="both" />
        </NamedAppLayoutErrorBoundary>
      )}

      {/* Intelligent Upgrade System - shows contextual upgrade prompts */}
      {user && (
        <NamedAppLayoutErrorBoundary name="IntelligentUpgradeSystem">
          <IntelligentUpgradeSystem />
        </NamedAppLayoutErrorBoundary>
      )}

      {shouldMountConsumerCopilot && (
        <NamedAppLayoutErrorBoundary name="ConsumerAICopilotGlobal">
          <ConsumerAICopilotGlobal />
        </NamedAppLayoutErrorBoundary>
      )}

      {/* Main Content */}
      <main className="pb-20 md:pb-0">
        <CloudBootstrapBanner />
        {isEnterpriseShell ? <EnterpriseTopbarBadge /> : null}
        <DSPageSection className="mx-auto w-full max-w-[1600px] px-0 sm:px-2 lg:px-4">
          {routeBody}
        </DSPageSection>
      </main>
    </DSPage>
  );
}